// Роутера нет: пять экранов и одно состояние. Так же это переезжает в Expo.
//
// «Проверка» 11 сентября 2026 стала состоянием экрана задачи — вердикт встаёт
// под тем же условием, и менять весь экран ради него нечего. В тот же день
// счёт вырос обратно: добавилась «История».
//
// Три из пяти — дневной цикл, и ученика ведут по ним сами задачи. Два
// остальных, «Прогресс» и «История», — единственные, куда он уходит по своей
// воле; первый отвечает, где он на карте, вторая — что он решал. Роутер от
// этого не нужен: экран остаётся значением в том же состоянии, и адрес его не
// помнит намеренно — ссылка на «свой прогресс», открытая в середине задачи,
// теряет ответ.

import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { api, useMutation, useQuery } from "./api";
import { activeTwinsForPlan, updateActiveTwins, type ActiveTwin } from "./lib/active-twin";
import { readLearnerKey } from "./lib/learner";
import { TaskClocks } from "./lib/task-clock";
import { atlasEntrySkill, newAtlasMemory } from "./lib/atlas-navigation";
import { Commitment } from "./screens/Commitment";
import { Drill } from "./screens/Drill";
import { FunctionLab } from "./screens/FunctionLab";
import { DayEnd } from "./screens/DayEnd";
import { Control } from "./screens/Control";
import { Diagnostic } from "./screens/Diagnostic";
import { History } from "./screens/History";
import { Profile } from "./screens/Profile";
import { Scratchpad } from "./screens/Scratchpad";
import { Plan } from "./screens/Plan";
import { TargetPick } from "./screens/TargetPick";
import { Progress } from "./screens/Progress";
import { Solve } from "./screens/Solve";
import { TestOut } from "./screens/TestOut";
import { Theory } from "./screens/Theory";
import { Today } from "./screens/Today";
import { AppNav, TabBar, type NavDestination } from "./ui/AppNav";
import { Dots } from "./ui/Dots";
import { NoKey } from "./ui/NoKey";
import { Shell } from "./ui/Shell";
import { Feedback } from "./ui/Feedback";
import { Messages } from "./ui/Messages";

// Вида «проверка» здесь нет с 11 сентября 2026: вердикт показывается на
// экране задачи, под её условием, и отдельным состоянием приложения не
// является. Пятый экран из шапки этого файла тем самым схлопнулся в четвёртый.
type AtlasView = {
  name: "atlas";
  skillId: string | null;
  backToItemKey: string | null;
  backToPlanDate: string | null;
};
type View =
  | { name: "today" }
  | { name: "solve"; itemKey: string; returnTo?: AtlasView }
  | { name: "day"; date: string }
  // Отдельной страницы «Состав набора» здесь больше нет: с 20 сентября 2026 это
  // свёрнутая секция на «Прогрессе» (src/ui/SetCard.tsx). Страница описывала
  // день, но ничего в нём не делала, а кнопка к ней стояла рядом с «Начать».
  | { name: "progress" }
  // План по месяцам (src/screens/Plan.tsx), решение 28 октября 2026.
  | { name: "plan" }
  // Личные данные: одно поле — имя. Не настройки: настройка меняет поведение
  // системы, а здесь меняется обращение в шапке «Сегодня». В адресе её нет по
  // той же причине, что у прогресса.
  | { name: "profile" }
  // «История» — второй экран, куда ученик уходит по своей воле. В адресе её
  // тоже нет: причина та же, что у прогресса — ссылка, открытая в середине
  // задачи, теряет ответ.
  | { name: "history" }
  // Разбор темы. В адресе его тоже нет — причина та же, что у прогресса и
  // истории: ссылка, открытая посреди задачи, теряет ответ.
  // `open` едет вместе с темой: страница разбора кончается разными кнопками —
  // «Решать эту тему» у открытой, «Сдать зачёт» у закрытой, — а спрашивать
  // сервер об этом значило бы читать состояние навыка ради одной кнопки.
  // `backTo` — задача, с которой ушли читать. `null` — пришли с карты тем.
  // Разбор с задачи возвращает на ту же задачу: см. кнопку в src/screens/Solve.tsx.
  | { name: "theory"; skillId: string; open: boolean; backTo: string | null; returnTo?: AtlasView }
  // Атлас сохраняет точный вход. Выбор узлов не меняет прогресс; занятия
  // запускают существующие функции и возвращаются в ту же точку карты.
  | AtlasView
  // Тренажёр по теме: задачи подряд, с разбором, ничего не двигающие. В
  // адресе его нет по той же причине, что у прочих: ссылка, открытая посреди
  // задачи, теряет ответ. `returnTo` хранит карту, откуда начали занятие.
  | { name: "drill"; skillId: string; returnTo?: AtlasView }
  // Лаборатория графиков. Состояние живёт в localStorage, сервер о ней не
  // знает — поэтому в адресе её нет по той же причине, что у прочих: ссылка,
  // открытая посреди задачи, теряет ответ.
  | { name: "graph" }
  // Черновики сами по себе, вне задачи. Состояние живёт в sessionStorage —
  // в адресе экрана нет по той же причине, что у прочих.
  | { name: "scratch" }
  | { name: "testout"; returnTo?: AtlasView };

// ПРОТОТИП. Черновик к задаче, `?scratch=A|B|C`. Три варианта живут все три —
// решение 3 сентября 2026. Удалять их вместе с
// src/screens/Solve.prototype.tsx и src/screens/scratch.prototype/ придётся
// только когда черновик поедет в продакшен настоящим, одним и оформленным.
//
// Динамический импорт стоит ВНУТРИ ветки `import.meta.env.DEV`, а не просто
// под `lazy()`, и разница измерена. Vite подставляет вместо `DEV` литерал
// `false`, Rollup выбрасывает мёртвую ветку целиком — и вместе с ней чанки.
// Раньше `lazy()` стоял снаружи, чанки собирались всегда, а загрузиться не
// могли: гейт `scratchRequested()` содержит тот же `DEV`. Цена была
// 30 МБ и 320 файлов в `dist/` против 4,1 МБ и 65, сборка 7,4 с против 1,2 —
// за 26 мегабайт, до которых в проде нет пути.
//
// `?map=1` рядом НЕ под гардом, и это не забывчивость: авторскую карту нужно
// смотреть с прода и с любого устройства, поэтому её 140 КБ в бандле законны.
const SolvePrototype = import.meta.env.DEV
  ? lazy(() => import("./screens/Solve.prototype"))
  : null;

// Отдельный вход в уроки: чанк загружается только по запросу, дневной цикл не запускается.
const LearningPilot = lazy(() => import("./screens/LearningPilot"));

function scratchRequested(): boolean {
  return (
    import.meta.env.DEV &&
    new URLSearchParams(window.location.search).has("scratch")
  );
}

/** `?control=1` — экран задач-контролёров. Инструмент взрослого, не ученика. */
function controlRequested(): boolean {
  return new URLSearchParams(window.location.search).get("control") === "1";
}

// `?map=1` — карта навыков. Инструмент автора, как и контролёры: не под
// DEV-гардом, потому что смотреть карту нужно с прода и с любого устройства.
// Ученику она ничего не ломает: в браузер уезжает граф, а каталог типовых
// ошибок остаётся на сервере.
//
// Через lazy() — раскладка весит 140 КБ, и в основном бандле ей делать
// нечего: она уходит отдельным куском, который грузится только под флагом.
const SkillMapAuthor = lazy(() =>
  import("./screens/SkillMap.author").then((m) => ({
    default: m.SkillMapAuthor,
  })),
);

// Ученический атлас тянет раскладку карты только по явному входу с задачи.
// Экран имеет named export, поэтому адаптируем его для React.lazy здесь.
const StudentSkillAtlas = lazy(() =>
  import("./screens/StudentSkillAtlas").then((m) => ({
    default: m.StudentSkillAtlas,
  })),
);

function mapRequested(): boolean {
  return new URLSearchParams(window.location.search).get("map") === "1";
}

export function App() {
  const learnerKey = useMemo(readLearnerKey, []);
  const control = useMemo(controlRequested, []);
  const scratch = useMemo(scratchRequested, []);
  const map = useMemo(mapRequested, []);
  const learningPilot = useMemo(() =>
    new URLSearchParams(window.location.search).get("learningPilot") === "1", []);
  // Ключа нет — спрашивать сервер не о чем: `resolveLearner` ответит отказом,
  // и отказ этот прилетит броском из рендера подписки. Раньше так и было, и
  // приложение гасло целиком. Карте и прототипу ключ не нужен вовсе.
  const noKey = learnerKey === "" && !(scratch || map);
  // Диагностика — вход в систему, а не отдельная функция: до неё состояния
  // ученика нет, и набор дня собирать не из чего.
  // ПРОТОТИП: под `?scratch=` запрос не идёт — прототип смотрят без Convex.
  // Под `?map=1` тоже: структурный вид карты состояния ученика не спрашивает.
  const diagnostic = useQuery(
    api.diagnostic.current,
    scratch || map || learningPilot || noKey ? "skip" : { learnerKey },
  );
  // Обязательство «сколько дней в неделю» — шаг после диагностики, а не
  // настройка: это первое и единственное решение, которое ученик принимает
  // сам. Одна строка `learners`, подписка на неё перезапускается только когда
  // обязательство меняли, то есть раз в неделю в самом частом случае.
  const commitment = useQuery(
    api.goal.current,
    scratch || map || learningPilot || noKey ? "skip" : { learnerKey },
  );
  const [stage, setStage] = useState<
    "loading" | "target" | "diagnostic" | "commitment" | "app"
  >("loading");
  useEffect(() => {
    if (diagnostic === undefined || commitment === undefined) return;
    // Решается один раз, при загрузке. Дальше экран меняет ученик кнопкой:
    // подписка, переключающая экран под руками, — это потерянный ответ.
    setStage((s) => {
      if (s !== "loading") return s;
      // Цель подготовки — первый шаг, до диагностики (решение 30 октября 2026).
      // Спрашивается только у того, кто ещё не начинал: прежние ученики без
      // поля считаются вступительным, и экран не встаёт у них поперёк дня.
      if (diagnostic === null && commitment.target === null) return "target";
      if (diagnostic === null || diagnostic.status !== "done") return "diagnostic";
      return commitment.daysPerWeek === null ? "commitment" : "app";
    });
  }, [diagnostic, commitment]);

  // Дата начатого набора закрепляется на всё решение. Иначе вкладка, открытая
  // до полуночи, показывает вчерашние задачи, а мутация уже пишет в сегодняшний
  // план: назначение не закрывается и его можно отправлять снова.
  const [pinnedPlanDate, setPinnedPlanDate] = useState<string | null>(null);
  // Близнец не входит в assignments, но должен пережить переход по черточкам
  // набора. Дата не даёт вчерашнему близнецу попасть в новый набор после полуночи.
  const [activeTwins, setActiveTwins] = useState<ActiveTwin[]>([]);
  // При возвращении домой меняем ключ подписки: ход часов сам по себе Convex-
  // запрос не перезапускает, даже если внутри него прочитан Date.now().
  const [planRefreshAt, setPlanRefreshAt] = useState(() => Date.now());
  // Узкий срез: один документ `dayPlans`. Подписка на состояние навыков
  // перезапускалась бы на каждый ответ и множила вызовы функций в 5–10 раз.
  const plan = useQuery(
    api.today.plan,
    stage === "app"
      ? pinnedPlanDate === null
        ? { learnerKey, refreshAt: planRefreshAt }
        : { learnerKey, date: pinnedPlanDate }
      : "skip",
  );
  // Идущий зачёт-вызов — один документ, подписка узкая. Она здесь, а не на
  // экране прогресса, ровно ради перезагрузки страницы: зачёт начат, вкладка
  // обновилась, и без этого ученик вернулся бы на карту, а зачёт остался бы
  // висеть незакрытым и не давал бы начать новый.
  //
  // Но только сегодняшний: вчерашний закрывается при входе (`testout.expire`,
  // замечание ученика №3 от 30 сентября 2026), и подписка ждёт этого закрытия —
  // иначе брошенный зачёт на миг забрал бы экран и не отдал: вид «testout»
  // сам назад не переключается.
  const expireTestOut = useMutation(api.testout.expire);
  const [staleTestOutClosed, setStaleTestOutClosed] = useState(false);
  useEffect(() => {
    if (stage !== "app" || staleTestOutClosed) return;
    void expireTestOut({ learnerKey })
      .catch((e: unknown) => console.error(e))
      .finally(() => setStaleTestOutClosed(true));
  }, [stage, staleTestOutClosed, expireTestOut, learnerKey]);
  const testout = useQuery(
    api.testout.current,
    stage === "app" && staleTestOutClosed ? { learnerKey } : "skip",
  );
  const [view, setView] = useState<View>({ name: "today" });
  const atlasMemory = useRef(newAtlasMemory(null));
  const lastSkill = useRef<string | null>(null);
  // Часы принадлежат задаче, а не экрану Solve: atlas и разбор размонтируют
  // экран, но не начинают решение заново. Во время ухода elapsed стоит, а wall
  // по-прежнему измеряет путь от первого показа задачи до отправки.
  const taskClocks = useRef(new TaskClocks());
  // Сообщение о автоматически добавленном навыке живёт выше Today: экран
  // размонтируется сразу после входа в задачу, а ученик должен успеть прочитать,
  // почему набор изменился.
  const [startNotice, setStartNotice] = useState<{ skillId: string; name: string } | null>(
    null,
  );
  /**
   * Незакрытый зачёт забирает экран.
   *
   * Через состояние, а не вторым деревом. До 14 сентября 2026 здесь стоял
   * ранний возврат со СВОИМ `<TestOut>`, и это молча ломало итог зачёта:
   * сданный зачёт исчезает из подписки, ранний возврат перестаёт срабатывать,
   * экран размонтируется — вместе с результатом, который он только что
   * получил. Ученик видел не «зачтено», а набор дня. Найдено пробой в
   * браузере; ни один тест этого не видел, потому что каждая ветка по
   * отдельности вела себя верно.
   */
  useEffect(() => {
    if (testout == null) return;
    setView((v) => (v.name === "testout" ? v : {
      name: "testout",
      returnTo: v.name === "atlas" ? v : "returnTo" in v ? v.returnTo : undefined,
    }));
  }, [testout]);
  // Порция задач по теме. Живёт здесь, потому что в неё уходят с двух экранов
  // — из карточки «своей темы» и со страницы разбора, — и оба обязаны звать
  // одну и ту же мутацию с одними правилами.
  //
  // Объявлена рядом с остальными хуками, ДО ранних возвратов экрана-черновика
  // и карты автора. Стояла ниже — React падал «Rendered more hooks than during
  // the previous render» на первом же открытии, и tsc этого не ловит.
  const practice = useMutation(api.today.practice);
  // Зачёт со страницы разбора. Экран он забирает сам: `testout.current` —
  // живой запрос выше, и вид переключается, как только документ появился.
  const startTestOut = useMutation(api.testout.start);

  if (scratch && SolvePrototype !== null) {
    return (
      <Shell context="Задача 3 из 7">
        <Suspense fallback={<div className="skeleton" style={{ width: 180 }} />}>
          <SolvePrototype />
        </Suspense>
      </Shell>
    );
  }

  if (map) {
    return (
      <Shell context="Карта навыков">
        <Suspense fallback={<div className="skeleton" style={{ width: 180 }} />}>
          <SkillMapAuthor />
        </Suspense>
      </Shell>
    );
  }

  // Раньше `?control=1` и обычный вход без ключа одинаково падали в консоль.
  if (noKey) {
    return (
      <Shell>
        <NoKey reason="missing" />
      </Shell>
    );
  }

  if (learningPilot) {
    return (
      <Shell context="Уроки и тренировки">
        <Suspense fallback={<div className="skeleton" aria-label="Загрузка урока" />}>
          <LearningPilot learnerKey={learnerKey} />
        </Suspense>
      </Shell>
    );
  }

  if (control) {
    return (
      <Shell context="Контролёры">
        <Control learnerKey={learnerKey} />
      </Shell>
    );
  }

  if (stage !== "app") {
    return (
      <Shell
        feedback={
          stage === "loading" ? undefined : (
            <Feedback
              as={{ learnerKey }}
              place={{
                screen: stage,
                where:
                  stage === "target" ? "Цель подготовки" : stage === "diagnostic" ? "Диагностика" : "Обязательство",
              }}
            />
          )
        }
      >
        {stage === "target" ? (
          <TargetPick learnerKey={learnerKey} onDone={() => setStage("diagnostic")} />
        ) : stage === "diagnostic" ? (
          <Diagnostic
            learnerKey={learnerKey}
            // После диагностики — обязательство, и только потом первый день:
            // ученик впервые видит, где он на карте, и берёт его на себя, пока
            // это ещё решение, а не отчёт.
            //
            // Кроме пересдачи: обязательство уже взято, и переписывать его
            // чаще раза в неделю нельзя — шаг показал бы отказ вместо выбора.
            onFinished={() =>
              setStage(
                commitment != null && commitment.daysPerWeek !== null
                  ? "app"
                  : "commitment",
              )
            }
          />
        ) : stage === "commitment" ? (
          <Commitment learnerKey={learnerKey} onDone={() => setStage("app")} />
        ) : (
          <div className="stack">
            <div className="skeleton" style={{ width: 180 }} />
            <div className="card">
              <div className="skeleton" />
            </div>
          </div>
        )}
      </Shell>
    );
  }

  const assignments = plan?.assignments ?? [];
  const activeTwinItemKeys = activeTwinsForPlan(activeTwins, plan?.date);
  const currentItemKey = view.name === "solve" ? view.itemKey : undefined;

  const position = assignments.findIndex((a) => a.itemKey === currentItemKey);
  /*
    Имя экрана в шапке — с 24 сентября 2026 для ВСЕХ экранов ученика, а не для
    трёх из десяти. Заголовок в теле страницы его повторял: «Графики» под
    словом «Лаборатория», «Личные данные» под «Личные данные», «Прогресс по
    карте» под пустым местом. Один раз в одном месте, дальше сразу содержимое.

    Ни серий, ни прогноза балла здесь по-прежнему нет: это имя экрана, а не
    строка состояния.
  */
  const context =
    view.name === "testout"
      ? "Зачёт"
      : view.name === "drill"
        ? "Тренажёр"
        : view.name === "atlas"
          ? "Атлас навыков"
          : view.name === "graph"
            ? "Лаборатория"
            : view.name === "scratch"
              ? "Черновики"
              : view.name === "theory"
                ? "Разбор темы"
                : view.name === "history"
                  ? "История"
                  : view.name === "profile"
                    ? "Личные данные"
                    : view.name === "plan"
                      ? "План"
                    : view.name === "progress"
                      ? "Прогресс по карте"
                      : view.name === "day"
                        ? "Итог дня"
                        : currentItemKey
                          ? position >= 0
                            ? `Задача ${position + 1} из ${assignments.length}`
                            : "Задача-близнец"
                          : undefined;

  // Черточки — единственный способ вернуться к уже закрытой задаче: набор до
  // 11 сентября 2026 шёл только вперёд. Закрытая открывается записью ответа,
  // незакрытая — на решение; разбирается с этим сам экран задачи.
  const dots =
    currentItemKey && assignments.length > 0 ? (
      <Dots
        assignments={assignments}
        activeTwinItemKeys={activeTwinItemKeys}
        currentItemKey={currentItemKey}
        onPick={(itemKey) => setView({ name: "solve", itemKey, returnTo: "returnTo" in view ? view.returnTo : undefined })}
      />
    ) : undefined;

  const today = () => {
    setStartNotice(null);
    setPinnedPlanDate(null);
    setPlanRefreshAt(Date.now());
    setView({ name: "today" });
  };
  const solve = (
    itemKey: string,
    addedSkill?: { skillId: string; name: string } | null,
    returnTo?: AtlasView,
    planDate?: string | null,
  ) => {
    setStartNotice(addedSkill ?? null);
    setPinnedPlanDate(planDate ?? plan?.date ?? null);
    setView({ name: "solve", itemKey, returnTo });
  };
  const openDay = () =>
    setView({
      name: "day",
      date: plan?.date ?? new Date().toISOString().slice(0, 10),
    });
  const openProgress = () => setView({ name: "progress" });
  const openAtlas = (skillId: string | null, backToItemKey: string | null = null) => {
    atlasMemory.current = newAtlasMemory(skillId);
    if (skillId) lastSkill.current = skillId;
    setView({ name: "atlas", skillId, backToItemKey, backToPlanDate: backToItemKey ? pinnedPlanDate ?? plan?.date ?? null : null });
  };
  const returnTo = "returnTo" in view ? view.returnTo : undefined;
  const returnFromActivity = returnTo ? () => setView(returnTo) : today;

  /*
    Ряд переходов в шапке — на всех экранах ученика, решение владельца от
    24 сентября 2026. До этого четыре подписи стояли в правом верхнем углу
    «Сегодня» и существовали только там: из задачи, разбора, тренажёра и
    лаборатории до истории и черновика хода не было вовсе.

    Инструментам автора (`?map=1`, `?control=1`), прототипу и экрану без ключа
    ряд не отдаётся: там нечему переключаться — их Shell вызывается выше и
    `nav` не получает.

    `pinnedPlanDate` переходом НЕ сбрасывается, и это не забывчивость: уйти с
    начатой задачи в историю и вернуться назад по «Траектории» должно вести в
    тот же набор, а не в пересобранный после полуночи.

    Исключение — «Решать»: он ведёт себя РОВНО как имя продукта, то есть зовёт
    `today()` со сбросом закрепления и пересборкой плана. Так и задумано: это
    не «вернуться откуда ушёл», а «сесть за сегодняшний набор».
  */
  const navCurrent: NavDestination | null =
    view.name === "atlas"
      ? "atlas"
      : view.name === "today"
      ? "today"
      : view.name === "graph"
        ? "graph"
        : view.name === "scratch"
          ? "scratch"
          : view.name === "history"
            ? "history"
            : view.name === "profile"
              ? "profile"
              : view.name === "plan"
                ? "plan"
              : view.name === "progress"
                ? "progress"
                : null;
  const goTo = (to: NavDestination) => {
    if (to === "atlas") {
      if (view.name === "atlas") return;
      if (returnTo) { setView(returnTo); return; }
      const skillId = view.name === "theory" || view.name === "drill" ? view.skillId
        : atlasEntrySkill(lastSkill.current, plan?.chosenSkillId ?? null, (plan?.opened ?? []).map((s) => s.skillId));
      openAtlas(skillId, view.name === "solve" ? view.itemKey : view.name === "theory" ? view.backTo : null);
      return;
    }
    if (to === "today") {
      today();
      return;
    }
    setStartNotice(null);
    setView(
      to === "graph"
        ? { name: "graph" }
        : to === "scratch"
          ? { name: "scratch" }
          : to === "history"
            ? { name: "history" }
            : to === "profile"
              ? { name: "profile" }
              : to === "plan"
                ? { name: "plan" }
                : { name: "progress" },
    );
  };
  // Одни и те же переходы — боковой колонкой на компьютере и нижней панелью
  // на телефоне (src/ui/AppNav.tsx).
  const nav = <AppNav current={navCurrent} onGo={goTo} />;
  const tabbar = <TabBar current={navCurrent} onGo={goTo} />;

  /*
    «Назад» шевроном слева от имени экрана — один выход на всех экранах
    ученика. Ведёт ОТКУДА ПРИШЛИ: разбор и атлас, открытые с задачи, возвращают
    в ту же задачу, всё остальное — в набор дня. Имя продукта рядом ведёт
    домой всегда, и это разные вопросы.

    Экранам потока шеврон не даётся. У зачёта и итога дня своя концовка, и
    выход в углу означал бы «бросить», не сказав этого вслух. У задачи выхода
    в шапке не было и не будет: ссылка над недорешённой задачей — приглашение
    заняться чем-то другим, и убрана она была именно за это.
  */
  const back = ((): { go: () => void; label: string } | null => {
    if (view.name === "theory" && view.backTo !== null) {
      const item = view.backTo;
      return { go: () => solve(item, null, returnTo), label: "К задаче" };
    }
    if (returnTo && view.name !== "testout") return { go: () => setView(returnTo), label: "К карте навыков" };
    if (view.name === "theory") {
      const item = view.backTo;
      return item === null
        ? { go: today, label: "К набору дня" }
        : { go: () => solve(item), label: "К задаче" };
    }
    if (view.name === "atlas") {
      const item = view.backToItemKey;
      return item === null
        ? { go: today, label: "К набору дня" }
        : { go: () => solve(item, null, undefined, view.backToPlanDate), label: "К задаче" };
    }
    return view.name === "graph" ||
      view.name === "scratch" ||
      view.name === "history" ||
      view.name === "profile" ||
      view.name === "progress" ||
      view.name === "plan" ||
      view.name === "drill"
      ? { go: today, label: "К набору дня" }
      : null;
  })();

  // Переходы в шапке приложения снова есть — с 24 сентября 2026, значками.
  // Дважды до этого их оттуда убирали, и оба раза за дело: ОДНА подпись рядом
  // с переключателем темы («Набор дня» или «Прогресс» смотря по экрану)
  // читалась как оформление, а четыре подписи в углу «Сегодня» жили только на
  // главной. Ряд одинаковых значков не похож ни на то, ни на другое, и стоит
  // на каждом экране. «Назад» перед заголовком экрана остаётся: он ведёт
  // откуда пришли, а ряд — куда захотели. Имя продукта по-прежнему ведёт домой.
  /*
    Обратная связь — на всех экранах ученика, включая задачу и зачёт: сбой
    замечают там, где он случился, и место уходит в замечание само. Задача —
    ключом (сервер по нему найдёт шаблон), тема — навыком; разбор, открытый с
    задачи, сообщает о теме, а не о задаче: читают разбор.
  */
  // Сообщения взрослого — рядом с замечанием: оба окна разговора, и кнопки
  // сообщений нет, пока взрослый не написал первым (src/ui/Messages.tsx).
  const feedback = (
    <>
      <Messages learnerKey={learnerKey} />
      <Feedback
        as={{ learnerKey }}
        place={{
          screen: view.name,
          where: context ?? "Сегодня",
          itemKey: currentItemKey,
          skillId:
            view.name === "theory" || view.name === "drill" || view.name === "atlas"
              ? view.skillId ?? undefined
              : undefined,
        }}
      />
    </>
  );

  return (
    <Shell
      context={context}
      right={dots}
      nav={nav}
      tabbar={tabbar}
      feedback={feedback}
      onHome={today}
      onBack={back?.go}
      backLabel={back?.label}
    >
      {view.name === "today" ? (
        <Today
          learnerKey={learnerKey}
          plan={plan}
          onStart={solve}
          onOpenDay={openDay}
          onOpenProgress={openProgress}
          onOpenTheory={(skillId, open) => {
            lastSkill.current = skillId;
            setView({ name: "theory", skillId, open, backTo: null });
          }}
          onOpenAtlas={(skillId) =>
            openAtlas(skillId)
          }
          onDrill={(skillId) => { lastSkill.current = skillId; setView({ name: "drill", skillId }); }}
          onOpenPlan={() => setView({ name: "plan" })}
        />
      ) : null}

      {view.name === "graph" ? <FunctionLab /> : null}

      {view.name === "scratch" ? <Scratchpad /> : null}

      {view.name === "theory" ? (
        <Theory
          learnerKey={learnerKey}
          skillId={view.skillId}
          open={view.open}
          backToItemKey={view.backTo}
          onBackToItem={(itemKey) => solve(itemKey, null, returnTo)}
          onTestOut={(skillId) => {
            void startTestOut({ learnerKey, skillId }).catch((e: unknown) =>
              console.error(e),
            );
          }}
          onDrill={(skillId) => setView({ name: "drill", skillId, returnTo })}
          onPractice={(skillId) => {
            // Порция берётся здесь, а не на экране разбора: мутация одна и та
            // же (`today.practice`), и второй её вызыватель разъехался бы с
            // первым на первой же правке правил добавки.
            void practice({ learnerKey, skillId })
              .then((r) => solve(r.firstItemKey, null, returnTo, r.date))
              .catch((e: unknown) => console.error(e));
          }}
          onToday={returnFromActivity}
          returnLabel={returnTo ? "К карте навыков" : undefined}
        />
      ) : null}

      {view.name === "solve" && startNotice !== null ? (
        <div className="notice notice--info" role="status" aria-live="polite" style={{ marginBottom: 16 }}>
          Добавлен навык «{startNotice.name}». Задачи по нему вошли в сегодняшний
          набор.
        </div>
      ) : null}

      {view.name === "solve" && plan != null ? (
        <Solve
          learnerKey={learnerKey}
          planDate={plan.date}
          itemKey={view.itemKey}
          onSkill={(skillId) => { lastSkill.current = skillId; }}
          clock={taskClocks.current.get(view.itemKey)}
          assignments={assignments}
          activeTwinItemKeys={activeTwinItemKeys}
          onAnswered={(answered) => {
            taskClocks.current.complete(answered.itemKey);
            setStartNotice(null);
            setActiveTwins((current) => updateActiveTwins(current, answered));
          }}
          onSolve={(itemKey) => setView({ name: "solve", itemKey, returnTo })}
          onOpenDay={openDay}
          onToday={today}
          // Тема задачи открыта по построению: в набор дня закрытые не
          // попадают. Спрашивать сервер об этом ради одной кнопки незачем —
          // и концовку у разбора, открытого с задачи, решает не она.
          onOpenTheory={(skillId, backTo) =>
            setView({ name: "theory", skillId, open: true, backTo, returnTo })
          }
          onOpenAtlas={(skillId, backToItemKey) => {
            if (returnTo) setView(returnTo);
            else openAtlas(skillId, backToItemKey);
          }}
        />
      ) : null}

      {view.name === "atlas" ? (
        <Suspense fallback={<div className="skeleton" style={{ width: 180 }} />}>
          <StudentSkillAtlas
            learnerKey={learnerKey}
            currentSkillId={view.skillId}
            memory={atlasMemory.current}
            onTheory={(skillId, open) => setView({ name: "theory", skillId, open, backTo: null, returnTo: view })}
            onDrill={(skillId) => setView({ name: "drill", skillId, returnTo: view })}
            onTestout={async (skillId) => {
              await startTestOut({ learnerKey, skillId });
              setView({ name: "testout", returnTo: view });
            }}
            onPractice={async (skillId) => {
              const result = await practice({ learnerKey, skillId });
              solve(result.firstItemKey, null, view, result.date);
            }}
          />
        </Suspense>
      ) : null}

      {view.name === "day" ? (
        <DayEnd
          learnerKey={learnerKey}
          date={view.date}
          plan={plan}
          onToday={today}
          onOpenProgress={openProgress}
        />
      ) : null}

      {view.name === "history" ? (
        <History
          learnerKey={learnerKey}
          onToday={today}
          onProgress={openProgress}
        />
      ) : null}

      {view.name === "plan" ? (
        <Plan learnerKey={learnerKey} onOpenAtlas={(skillId) => openAtlas(skillId)} />
      ) : null}

      {view.name === "progress" ? (
        <Progress
          learnerKey={learnerKey}
          plan={plan}
          onToday={today}
          onTestOut={() => setView({ name: "testout" })}
          onDrill={(skillId) => setView({ name: "drill", skillId })}
          onSolve={solve}
          onRestartDiagnostic={() => {
            setView({ name: "today" });
            setStage("diagnostic");
          }}
        />
      ) : null}

      {view.name === "profile" ? (
        <Profile learnerKey={learnerKey} />
      ) : null}

      {/*
        Занятие, начатое с карты, возвращается к выбранному узлу. Остальные
        входы сохраняют прежний выход в набор дня.
      */}
      {view.name === "drill" ? (
        <Drill
          key={view.skillId}
          learnerKey={learnerKey}
          skillId={view.skillId}
          onToday={returnFromActivity}
          returnLabel={returnTo ? "К карте навыков" : undefined}
          // Открытость едет с темой, как и везде: тренажёр по закрытой теме —
          // обычное дело, а страница разбора кончается кнопками по состоянию
          // темы. Соври здесь `open: true` — и у закрытой встанет «Решать эту
          // тему», которая откажет после нажатия.
          onOpenTheory={(skillId, open) =>
            setView({ name: "theory", skillId, open, backTo: null, returnTo })
          }
          onTestOut={(skillId) => {
            void startTestOut({ learnerKey, skillId }).catch((e: unknown) =>
              console.error(e),
            );
          }}
        />
      ) : null}

      {view.name === "testout" ? (
        <TestOut learnerKey={learnerKey} onDone={returnFromActivity} returnLabel={returnTo ? "К карте навыков" : undefined} />
      ) : null}
    </Shell>
  );
}
